import { useEffect, useMemo, useRef, useState } from "react";
import {
  BellIcon,
  CheckIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

export type NotificationTone = "default" | "info" | "success" | "warning" | "danger";

export interface AppNotification {
  id: string;
  title: string;
  description?: string;
  createdAt: string | Date;
  unread?: boolean;
  tone?: NotificationTone;
  href?: string;
  initials?: string;
}

interface NotificationsDropdownProps {
  notifications: AppNotification[];
  onNotificationClick?: (notification: AppNotification) => void;
  onMarkAllAsRead?: () => void;
  onViewAll?: () => void;
  className?: string;
  maxHeightClassName?: string;
}

export default function NotificationsDropdown({
  notifications,
  onNotificationClick,
  onMarkAllAsRead,
  onViewAll,
  className = "",
  maxHeightClassName = "max-h-[420px]",
}: NotificationsDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.unread).length,
    [notifications]
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
        aria-label="Open notifications"
        aria-expanded={open}
      >
        <BellIcon className="h-5 w-5" />

        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white shadow-sm">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-[420px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Notifications</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  {unreadCount > 0
                    ? `You have ${unreadCount} unread update${unreadCount === 1 ? "" : "s"}`
                    : "You’re all caught up"}
                </p>
              </div>

              {notifications.length > 0 && onMarkAllAsRead && unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => onMarkAllAsRead()}
                  className="rounded-xl bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-100"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          {notifications.length === 0 ? (
            <EmptyNotificationsState />
          ) : (
            <div className={`${maxHeightClassName} overflow-y-auto p-2`}>
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onClick={() => {
                    onNotificationClick?.(notification);
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          )}

          {notifications.length > 0 && onViewAll && (
            <div className="border-t border-slate-100 p-3">
              <button
                type="button"
                onClick={() => {
                  onViewAll();
                  setOpen(false);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                View all notifications
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onClick,
}: {
  notification: AppNotification;
  onClick: () => void;
}) {
  const toneClasses = getToneClasses(notification.tone ?? "default");

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition ${
        notification.unread ? "bg-violet-50/60 hover:bg-violet-50" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-xs font-bold ring-1 ${toneClasses}`}
      >
        {notification.initials || getInitials(notification.title)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">{notification.title}</p>
            {notification.description && (
              <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500">
                {notification.description}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2 pt-1">
            {notification.unread && <span className="h-2.5 w-2.5 rounded-full bg-violet-500" />}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-slate-400">
            {formatNotificationTime(notification.createdAt)}
          </span>

          <span className="text-xs font-medium text-violet-600 opacity-0 transition group-hover:opacity-100">
            View
          </span>
        </div>
      </div>
    </button>
  );
}

function EmptyNotificationsState() {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <CheckIcon className="h-7 w-7" />
      </div>
      <h4 className="mt-4 text-sm font-semibold text-slate-800">No notifications</h4>
      <p className="mt-1 text-sm text-slate-500">New alerts and work order updates will show up here.</p>
    </div>
  );
}

function getToneClasses(tone: NotificationTone) {
  switch (tone) {
    case "info":
      return "bg-blue-50 text-blue-700 ring-blue-100";
    case "success":
      return "bg-emerald-50 text-emerald-700 ring-emerald-100";
    case "warning":
      return "bg-amber-50 text-amber-700 ring-amber-100";
    case "danger":
      return "bg-rose-50 text-rose-700 ring-rose-100";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

function getInitials(value: string) {
  return value
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function formatNotificationTime(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Just now";
  }

  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "Just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < day * 7) return `${Math.floor(diffMs / day)}d ago`;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function NotificationsDropdownExample() {
  const [notifications, setNotifications] = useState<AppNotification[]>([
    {
      id: "1",
      title: "Work order overdue",
      description: "WO-0004 is now 1h 38m overdue.",
      createdAt: new Date(Date.now() - 2 * 60 * 1000),
      unread: true,
      tone: "danger",
      initials: "WO",
    },
    {
      id: "2",
      title: "Assigned to you",
      description: "Ben Johns assigned you to 'An Assigned Test'.",
      createdAt: new Date(Date.now() - 14 * 60 * 1000),
      unread: true,
      tone: "info",
      initials: "AJ",
    },
    {
      id: "3",
      title: "Work order completed",
      description: "'test' was marked complete by Josh Franklin-Martin.",
      createdAt: new Date(Date.now() - 60 * 60 * 1000),
      unread: false,
      tone: "success",
      initials: "OK",
    },
  ]);

  return (
    <div className="flex items-center justify-end p-8">
      <NotificationsDropdown
        notifications={notifications}
        onNotificationClick={(notification) => {
          setNotifications((current) =>
            current.map((item) =>
              item.id === notification.id ? { ...item, unread: false } : item
            )
          );
        }}
        onMarkAllAsRead={() => {
          setNotifications((current) =>
            current.map((notification) => ({ ...notification, unread: false }))
          );
        }}
        onViewAll={() => {
          console.log("Navigate to notifications page");
        }}
      />
    </div>
  );
}
